import React, { createContext, useContext, ReactNode, useState } from 'react';

// Define equipment types
export interface Equipment {
  id: string;
  name: string;
  type: string;
  status: 'available' | 'in-use' | 'maintenance' | 'damaged';
  location?: string;
  assigned_to?: string;
  purchase_date?: string;
  notes?: string;
}

// Define the equipment context type
interface EquipmentContextType {
  equipment: Equipment[];
  loading: boolean;
  error: string | null;
  addEquipment: (item: Omit<Equipment, 'id'>) => void;
  updateEquipment: (id: string, updates: Partial<Equipment>) => void;
  deleteEquipment: (id: string) => void;
  assignEquipment: (id: string, assignedTo: string) => void;
  changeStatus: (id: string, status: Equipment['status']) => void;
}

// Create the context with default values
const EquipmentContext = createContext<EquipmentContextType>({
  equipment: [],
  loading: false,
  error: null,
  addEquipment: () => {},
  updateEquipment: () => {},
  deleteEquipment: () => {},
  assignEquipment: () => {},
  changeStatus: () => {},
});

// Hook to use the equipment context
export const useEquipment = () => useContext(EquipmentContext);

// Provider component
export const EquipmentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [equipment, setEquipment] = useState<Equipment[]>([
    {
      id: '1',
      name: 'Langstroth Hive',
      type: 'hive',
      status: 'in-use',
      location: 'Meadow Apiary',
      purchase_date: '2024-01-15',
    },
    {
      id: '2',
      name: 'Smoker',
      type: 'tool',
      status: 'available',
      purchase_date: '2023-10-05',
    },
    {
      id: '3',
      name: 'Honey Extractor',
      type: 'harvesting',
      status: 'available',
      purchase_date: '2024-02-20',
      notes: 'Manual 4-frame radial extractor',
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add a new equipment item
  const addEquipment = (item: Omit<Equipment, 'id'>) => {
    try {
      setLoading(true);
      // Generate a random ID for demo purposes
      const newItem: Equipment = {
        ...item,
        id: Math.random().toString(36).substring(2, 9),
      };
      setEquipment(prev => [...prev, newItem]);
    } catch (err) {
      setError('Failed to add equipment');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Update an equipment item
  const updateEquipment = (id: string, updates: Partial<Equipment>) => {
    try {
      setLoading(true);
      setEquipment(prev =>
        prev.map(item =>
          item.id === id ? { ...item, ...updates } : item
        )
      );
    } catch (err) {
      setError('Failed to update equipment');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Delete an equipment item
  const deleteEquipment = (id: string) => {
    try {
      setLoading(true);
      setEquipment(prev => prev.filter(item => item.id !== id));
    } catch (err) {
      setError('Failed to delete equipment');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Assign equipment to someone/something
  const assignEquipment = (id: string, assignedTo: string) => {
    updateEquipment(id, { assigned_to: assignedTo, status: 'in-use' });
  };

  // Change equipment status
  const changeStatus = (id: string, status: Equipment['status']) => {
    updateEquipment(id, { status });
  };

  return (
    <EquipmentContext.Provider
      value={{
        equipment,
        loading,
        error,
        addEquipment,
        updateEquipment,
        deleteEquipment,
        assignEquipment,
        changeStatus,
      }}
    >
      {children}
    </EquipmentContext.Provider>
  );
};
